import { api, type QueryParams } from "@/lib/api/client";

/**
 * Why somebody is working away from their own branch: HR arranged cover,
 * the rota puts them there, or their own branch needs nobody that day.
 */
export type MovedBy = "arranged" | "rota" | "automatic";

export interface RosterStaff {
  user_id: string;
  name: string;
  employee_number: string | null;
  covering_from: string | null;
  moved_by: MovedBy | null;
  note: string | null;
}

export interface RosterMovedOut {
  user_id: string;
  name: string;
  employee_number: string | null;
  to_branch: string;
  moved_by: MovedBy;
}

export interface RosterBranch {
  branch_id: string;
  branch_code: string;
  branch_name: string;
  /** The branch's standing minimum, as set on the branch. */
  min_headcount: number;
  /** What it needs that day, from Staffing needs or else the minimum. */
  needed: number;
  /** Needs nobody that day; its people are working elsewhere. */
  closed: boolean;
  /** Closed because it is moving premises, rather than for the day. */
  relocating?: boolean;
  reopening_expected_on?: string | null;
  headcount: number;
  understaffed: boolean;
  staff: RosterStaff[];
  moved_out: RosterMovedOut[];
}

export interface RosterAway {
  user_id: string;
  name: string;
  employee_number: string | null;
  reason: string;
}

export interface RosterDay {
  date: string;
  branches: RosterBranch[];
  on_leave: RosterAway[];
  resting: RosterAway[];
}

export interface Assignment {
  id: string;
  user_id: string;
  branch_id: string;
  start_date: string;
  end_date: string;
  note: string | null;
  employee_name: string | null;
  branch_name: string | null;
}

export const scheduleKeys = {
  all: ["schedule"] as const,
  roster: (from: string, to: string) => [...scheduleKeys.all, "roster", from, to] as const,
  assignments: (filters: QueryParams) => [...scheduleKeys.all, "assignments", filters] as const,
  mine: () => [...scheduleKeys.all, "mine"] as const,
};

export const scheduleApi = {
  roster: (from: string, to: string) =>
    api.get<RosterDay[]>("/schedule", { query: { from, to } }),

  assignments: (filters: QueryParams) =>
    api.get<Assignment[]>("/schedule/assignments", { query: filters }),

  createAssignment: (payload: {
    user_id: string;
    branch_id: string;
    start_date: string;
    end_date: string;
    note?: string | null;
  }) => api.post<Assignment>("/schedule/assignments", payload),

  cancelAssignment: (id: string) =>
    api.post<{ message: string }>(`/schedule/assignments/${id}/cancel`),

  myAssignments: () => api.get<Assignment[]>("/me/assignments"),
};
